import { useState, useEffect } from "react";
import { UserCog, Users, Trash2, Plus, AlertTriangle, TrendingUp, Video } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FathomKeyModal } from "./FathomKeyModal";

interface Closer {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string | null;
  performance_score: number | null;
  current_assignments: number | null;
  weekly_target: number | null;
  created_at: string;
  fathom_api_key_vault_ref: string | null;
  fathom_key_last_verified_at: string | null;
  fathom_last_synced_at: string | null;
}

export function ClosersManagementTab() {
  const [closers, setClosers] = useState<Closer[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedCloser, setSelectedCloser] = useState<Closer | null>(null);
  const [fathomModalCloser, setFathomModalCloser] = useState<Closer | null>(null);
  const [newCloser, setNewCloser] = useState({
    name: '',
    email: '',
    phone: '',
    weekly_target: 10
  });

  useEffect(() => {
    fetchClosers();
  }, []);

  const fetchClosers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('closers')
        .select('*')
        .order('name');

      if (error) throw error;
      setClosers(data || []);
    } catch (error) {
      console.error('Error fetching closers:', error);
      toast.error('Failed to load closers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCloser = async () => {
    if (!selectedCloser) return;

    try {
      // First check if closer has active assignments
      const { data: assignments } = await supabase
        .from('closer_assignments')
        .select('id')
        .eq('closer_id', selectedCloser.id)
        .eq('status', 'active');

      if (assignments && assignments.length > 0) {
        toast.error(`Cannot delete ${selectedCloser.name} - they have ${assignments.length} active client assignments. Reassign clients first.`);
        setDeleteDialogOpen(false);
        return;
      }

      // Delete the closer
      const { error } = await supabase
        .from('closers')
        .delete()
        .eq('id', selectedCloser.id);

      if (error) throw error;

      toast.success(`${selectedCloser.name} has been removed from the system`);
      setDeleteDialogOpen(false);
      setSelectedCloser(null);
      fetchClosers();
    } catch (error) {
      console.error('Error deleting closer:', error);
      toast.error('Failed to delete closer');
    }
  };

  const handleAddCloser = async () => {
    if (!newCloser.name || !newCloser.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const { error } = await supabase
        .from('closers')
        .insert({
          name: newCloser.name,
          email: newCloser.email,
          phone: newCloser.phone || null,
          weekly_target: newCloser.weekly_target,
          status: 'active',
          current_assignments: 0,
          performance_score: 0
        });

      if (error) throw error;

      toast.success(`${newCloser.name} added to the team`);
      setAddDialogOpen(false);
      setNewCloser({ name: '', email: '', phone: '', weekly_target: 10 });
      fetchClosers();
    } catch (error) {
      console.error('Error adding closer:', error);
      toast.error('Failed to add closer');
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'flagged':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Closers Management</h2>
          <p className="text-sm text-muted-foreground">Add, manage, and remove sales closers</p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Closer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-secondary/20">
              <Users className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{closers.length}</p>
              <p className="text-sm text-muted-foreground">Total Closers</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/20">
              <UserCog className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{closers.filter(c => c.status === 'active').length}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{closers.filter(c => c.status === 'flagged').length}</p>
              <p className="text-sm text-muted-foreground">Flagged</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/20">
              <TrendingUp className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {closers.length > 0 
                  ? Math.round(closers.reduce((sum, c) => sum + (c.performance_score || 0), 0) / closers.length)
                  : 0}%
              </p>
              <p className="text-sm text-muted-foreground">Avg Performance</p>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Closers Table */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>All Closers</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading closers...</p>
            </div>
          ) : closers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No closers in the system</p>
              <Button onClick={() => setAddDialogOpen(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Closer
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Closer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Assignments</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Weekly Target</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Performance</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Fathom</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {closers.map((closer) => (
                    <tr key={closer.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <UserCog className="w-5 h-5 text-primary" />
                          </div>
                          <p className="font-medium">{closer.name}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm">{closer.email}</p>
                        <p className="text-sm text-muted-foreground">{closer.phone || 'No phone'}</p>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {closer.current_assignments || 0}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {closer.weekly_target || 0}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={
                          (closer.performance_score || 0) >= 30 ? 'text-green-500 font-bold' :
                          (closer.performance_score || 0) >= 20 ? 'text-amber-500' :
                          'text-red-500'
                        }>
                          {closer.performance_score || 0}%
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={getStatusColor(closer.status)}>
                          {closer.status || 'Unknown'}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {closer.fathom_api_key_vault_ref ? (
                          <button
                            type="button"
                            className="inline-flex flex-col items-center gap-0.5 cursor-pointer"
                            onClick={() => setFathomModalCloser(closer)}
                          >
                            <BadgeVenture variant="success">Fathom Connected</BadgeVenture>
                            {closer.fathom_last_synced_at && (
                              <span className="text-xs text-muted-foreground">
                                Last sync: {new Date(closer.fathom_last_synced_at).toLocaleDateString()}
                              </span>
                            )}
                          </button>
                        ) : (
                          <Button
                            variant="ghost-border"
                            size="sm"
                            onClick={() => setFathomModalCloser(closer)}
                          >
                            <Video className="h-4 w-4 mr-1" />
                            Connect Fathom
                          </Button>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => {
                            setSelectedCloser(closer);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Remove Closer
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{selectedCloser?.name}</strong> from the system? 
              This action cannot be undone. Make sure they have no active client assignments first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCloser} className="bg-red-500 hover:bg-red-600">
              Remove Closer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Closer Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-surface border-border">
          <DialogHeader>
            <DialogTitle>Add New Closer</DialogTitle>
            <DialogDescription>Add a new sales closer to your team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Full Name *</label>
              <input
                type="text"
                value={newCloser.name}
                onChange={(e) => setNewCloser({ ...newCloser, name: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <input
                type="email"
                value={newCloser.email}
                onChange={(e) => setNewCloser({ ...newCloser, email: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <input
                type="tel"
                value={newCloser.phone}
                onChange={(e) => setNewCloser({ ...newCloser, phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Weekly Target</label>
              <input
                type="number"
                value={newCloser.weekly_target}
                onChange={(e) => setNewCloser({ ...newCloser, weekly_target: parseInt(e.target.value) || 10 })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background"
                min={1}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCloser}>Add Closer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fathom Key Modal */}
      <FathomKeyModal
        open={!!fathomModalCloser}
        onOpenChange={(open) => { if (!open) setFathomModalCloser(null); }}
        closer={fathomModalCloser}
        onSuccess={fetchClosers}
      />
    </div>
  );
}
